import React, { useState, useEffect } from 'react';
import { Permission, PermissionType } from 'lib/util';
import { Typography } from 'lib/components';
import { Checkbox } from 'lib/components/FormFields';
import { AllCheckedStatus } from 'types';
import classes from './index.module.scss';

/**
 * Props interface for the PermissionRow component
 */
interface Props {
	/** The permission type to display in this row */
	permission: PermissionType;
	/** List of currently selected permissions */
	list?: PermissionType[];
	/** Overall check status for the permission group ('all', 'none', or 'some') */
	status: AllCheckedStatus;
	/** Callback function when checkbox is toggled */
	checkAction: (id: PermissionType, isChecked: boolean) => void;
}

/**
 * PermissionRow component
 * Renders a single permission row with a checkbox for selection
 * Used in admin creation and editing screens for permission management
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const PermissionRow = ({ list = [], permission, status, checkAction }: Props) => {
	// Track checkbox checked state
	const [checked, setChecked] = useState(false);

	/**
	 * Update checked state when permission list changes
	 * Sets checkbox to checked if the permission is in the list
	 */
	useEffect(() => {
		if (list.length === 0) return;
		if (list.includes(permission)) setChecked(true);
		else setChecked(false);
	}, [list]);

	/**
	 * Update checked state when overall status changes
	 * Respects 'some' status (partial selection) but unchecks on 'none'
	 */
	useEffect(() => {
		if (status === 'some') return;
		if (status === 'none') setChecked(false);
	}, [status]);

	/**
	 * Handle checkbox change event
	 * Updates local state and calls parent callback
	 *
	 * @param {React.ChangeEvent<HTMLInputElement>} e - Checkbox change event
	 */
	const handleCheckAction = (e: React.ChangeEvent<HTMLInputElement>) => {
		setChecked(e.target.checked);
		checkAction(permission, e.target.checked);
	};

	return (
		<div className={classes['permissions-tableRow']}>
			{/* Permission name column */}
			<div className={classes['permissions-tableCol']}>
				<Typography>{Permission.text(permission)}</Typography>
			</div>
			{/* Checkbox column */}
			<div className={classes['permissions-tableCol']}>
				<Checkbox checked={checked} onChange={handleCheckAction} />
			</div>
			{/* Empty spacer column */}
			<div className={classes['permissions-tableCol']}>&nbsp;</div>
		</div>
	);
};

export default PermissionRow;
